import React, { useCallback, useMemo, useRef, useState, useContext } from 'react'
import { Body, Button, Select, Input, useOnOutsideClick, Bold, Link, useModal } from '@panwds/react-ui'
import {
  ConnectedTable,
  ConnectedTableLayout,
  TableProvider,
  ConnectedTableFilter,
  ConnectedPaginator,
} from '@panwds/react-table'
import { MoreIcon } from '@panwds/icons'
import { setCurrentViewing } from '../../common/utils/Sparky'
import { history } from '@sparky/framework'
import GlobalContext from '../../common/context/GlobalContext'
import V5TableWrapper from '../../common/components/table/V5TableWrapper'

import { AIOPS_APP_ID, ACTION_BUTTON_DAS, ACTION_BUTTON_CLOUD, ACTION_BUTTON_NOOP } from '../constants'
import { APP_ID_LOGGING_SERVICE, APP_ID_PRISMA_ACCESS, APP_ID_PRISMA_ACCESS_EDITION, APP_ID_SDWAN, APP_ID_TO_NAME, LABEL_NOT_APPLICABLE_SHORT } from './common/GlobalConstants'
import TableHeader from '../../common/components/table/TableHeader'
import { VIEW_DEVICE_ASSOCIATIONS } from '../../common/constants/GlobalConstants'
import AppImage from './unified/AppImage'
import AssociatedTenantModal from './AssociatedTenantModal'
import ContractModal from './ContractModal'
import { getVarValue } from '../../common/utils/Sparky'
import { FF_LICENSE_TABLE_LABEL } from '../../common/utils/FeatureFlags'
import type { VFC, ChangeEvent, KeyboardEvent, MouseEvent } from 'react'
import styled from 'styled-components'
//import type { AppManifest } from '@sparky/framework'
type LicenseTableProps = {
  apps: {
    [key: string]: any
  }
  tableData: LicenseTableData[]
  loading: boolean
}
export type LicenseTableData = {
  license_id: string
  app_id: string
  support_account_id: string
  support_account_name: string
  entitlements: any[]
  is_license_remaining?: boolean
  associated_tenants?: {
    [key: string]: {
      tsg_id: string
      pending_upgrade?: boolean
    }
  }
  csp_serial_number?: string
  label?: string
}

const TABLE_CONTAINER = styled(V5TableWrapper)`
  [style="min-height: 289px;"] {
    height: unset !important;
  }
`

const licenseLabel = getVarValue(FF_LICENSE_TABLE_LABEL, false)

const Label: VFC<{ value: string }> = (props) => {
  const MAX_LABEL_LENGTH = 25
  const { value } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const [isInputEnable, setInputEnable] = useState<boolean>(false)
  const [isInputVisible, setInputVisible] = useState<boolean>(Boolean(value))
  const [label, setLabel] = useState<string>(value)

  useOnOutsideClick(containerRef, () => {
    if (isInputEnable) {
      // set
      setInputEnable(false)
    }
  })
  const onEditLabel = (e: MouseEvent<HTMLDivElement>) => {
    setInputEnable(true)
  }
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > MAX_LABEL_LENGTH) {
      return
    }
    setLabel(e.target.value)
  }

  const onKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault() // Prevent form submission
      setInputEnable(false)
    }
  }

  return (
    <div
      className='cm-w-full cm-h-full cm-flex cm-justify-center cm-items-center'
      ref={containerRef}
      onDoubleClick={onEditLabel}
      onMouseEnter={() => setInputVisible(true)}
      onMouseLeave={() => {
        if (isInputEnable) {
          return
        }
        setInputVisible(Boolean(label))
      }}
    >
      {isInputVisible && <Input
        value={label}
        readOnly={!isInputEnable}
        onChange={onChange}
        onKeyDown={onKeyPress}
        placeholder='Create label'
      />}

    </div>
  )
}

const LicenseTable: VFC<LicenseTableProps> = (props) => {
  const { apps: appManifest, tableData, loading } = props
  const associatedTenantModal = useModal(AssociatedTenantModal)
  const contractModal = useModal(ContractModal)
  const { treeReducer = {} } = useContext(GlobalContext)
  const { sgKeyBy } = treeReducer

  const onSelectDas = useCallback(() => {
    setCurrentViewing(VIEW_DEVICE_ASSOCIATIONS)
  }, [])

  const onSelectActivate = useCallback((licenseId, app_id: string) => {
    const appIdToContext = (app_id: string) => {
      switch (app_id) {
        case APP_ID_PRISMA_ACCESS_EDITION:
          return APP_ID_PRISMA_ACCESS
        default:
          return app_id
      }
    }
    const context = appIdToContext(app_id)
    history.push(`/activation/licenses/${context}/${licenseId}`)
  }, [])

  const onActionSelect = useCallback((e, row) => {
    switch (e.selectedItem.value) {
      case ACTION_BUTTON_DAS:
        onSelectDas()
        break
      case ACTION_BUTTON_CLOUD:
        onSelectActivate(row.original.license_id, row.original.app_id)
        break
      default:
        return
    }
  }, [onSelectActivate, onSelectDas])

  const data = useMemo(() => {
    /**
     * construct any field that needs to be filerable
     * e.g. app_id => display_name, csp_name + csp_id => csp
     */
    return tableData.map(each => {
      const tsgInfo = each.associated_tenants ?
        Object.values(each.associated_tenants).filter(_each => sgKeyBy[_each.tsg_id]).map(_each => `${sgKeyBy[_each.tsg_id].display_name}:${_each.tsg_id}`).join('|') :
        undefined
      return {
        ...each,
        display_name: appManifest[each.app_id]?.display_name,
        support_account_info: `${each.support_account_name} - ${each.support_account_id}`,
        tsg_info: tsgInfo,
      }
    })
  }, [appManifest, sgKeyBy, tableData])

  /**
   * for PAE license, need to display multiple lines of entitlement product
   * @param props
   * @returns
   */
  const DisplayNameRenderer = useCallback((props: any) => {
    const { row, value } = props
    return (
      <div className='cm-w-full cm-my-1'>
        <div className='cm-flex cm-flex-row'>
          <AppImage app_id={row.original.app_id} apps={appManifest} alt={'logo'} style={{ height: '20px', objectFit: 'contain', width: '20px' }} />
          <Body addClassName='cm-ml-1'>{value}</Body>
        </div>
        {row.original.app_id === APP_ID_PRISMA_ACCESS_EDITION &&
          <div className='cm-ml-6 cm-mt-2 cm-flex cm-flex-col cm-w-full'>
            {row?.original?.entitlements?.map((each: any, index: number) => (
              <Body key={index} size='sm' addClassName='cm-w-[95%]' truncate>
                {each?.license_display_name || APP_ID_TO_NAME[each?.app_id]}
              </Body>
            ))}
          </div>
        }
      </div>
    )
  }, [appManifest])

  const QuantityRenderer = (props: any) => {
    const { row } = props
    if (!props.value || !props.value[0]) {
      return LABEL_NOT_APPLICABLE_SHORT
    }
    if (row.original.app_id === AIOPS_APP_ID) {
      return <Body>{props.value[0].remained_size}/{props.value[0].purchased_size} {props.value[0].purchased_unit}</Body>
    }
    if (row.original.app_id === APP_ID_PRISMA_ACCESS_EDITION) {
      // const paeEntitlement = props.value.find((each: any) => each.app_id === APP_ID_PRISMA_ACCESS_EDITION)
      // return <Body>{paeEntitlement.allocated_size}/{paeEntitlement.purchased_size}</Body>
      return (
        <div className='cm-my-1'>
          <div className='cm-h-4'></div>
          <div className='cm-mt-2 cm-flex cm-flex-col cm-w-full'>
            {props.value?.map((each: any, index: number) => (
              <Body key={index} size='sm'>
                {each?.allocated_size}/{each?.purchased_size}
              </Body>
            ))}
          </div>
        </div>
      )
    }
    return <Body>{props.value[0].allocated_size}/{props.value[0].purchased_size}</Body>
  }

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'display_name',
        Cell: DisplayNameRenderer,
        width: 200
      },
      {
        Header: 'Label',
        accessor: 'label',
        Cell: (props: any) => {
          return <Label value={props.value ?? ''} />
        },
        width: 150,
        defaultHidden: !licenseLabel
      },
      {
        Header: 'Status',
        accessor: 'is_license_remaining',
        Cell: (props: any) => <Body>{props.value ? 'Partially Assigned' : 'All Assigned'}</Body>,
        width: 125,
      },
      {
        Header: 'Assigned/Total QTY',
        accessor: 'entitlements',
        Cell: QuantityRenderer,
        width: 150
      },
      {
        Header: 'Customer Support Account',
        accessor: 'support_account_info',
        Cell: (props: any) => {
          return <Body>{props.value}</Body>
        },
        width: 275,
      },
      {
        Header: 'Expires',
        accessor: (row: any) => row.entitlements[0]?.license_expiration,
        Cell: (props: any) => {
          const { value } = props
          return <Body>{value || LABEL_NOT_APPLICABLE_SHORT}</Body>
        },
        width: 175
      },
      {
        Header: 'Associated Tenant',
        accessor: 'associated_tenants',
        Cell: (props: any) => {
          const { value, row } = props
          return (
            <Body>
              {value ?
                <Link onClick={() => (
                  associatedTenantModal.openModal({ associatedTenants: row.original.associated_tenants })
                )}
                >
                  <Bold>{Object.keys(value).length}</Bold>
                </Link> :
                LABEL_NOT_APPLICABLE_SHORT
              }
            </Body >
          )
        },
        width: 150,
      },
      {
        Header: 'TSG Info',
        accessor: 'tsg_info',
        Cell: (props: any) => <Body>{props.value ?? LABEL_NOT_APPLICABLE_SHORT}</Body>,
        width: 100,
        // hide this, only for filring purpose
        defaultHidden: true,
      },
      {
        Header: 'Contract',
        accessor: 'csp_serial_number',
        Cell: (props: any) => {
          const { value, row } = props
          if (row.original.app_id === APP_ID_PRISMA_ACCESS_EDITION) {
            const { support_account_id, support_account_name, csp_serial_number, license_id } = row.original
            return (
              <Link onClick={() => (
                contractModal.openModal({
                  displayInfo: {
                    support_account_id,
                    support_account_name,
                    license_id,
                    csp_serial_number,
                  }
                })
              )}
              >
                <Bold>{value || LABEL_NOT_APPLICABLE_SHORT}</Bold>
              </Link>
            )
          }
          return <Body>{LABEL_NOT_APPLICABLE_SHORT}</Body>
        },
        width: 150,
      },
      {
        Header: 'Actions',
        Cell: (props: any) => {
          const { row } = props
          const items = []
          if (row.original.app_id === APP_ID_LOGGING_SERVICE) {
            items.push({ value: ACTION_BUTTON_NOOP })
          }
          else if (row.original.app_id === APP_ID_PRISMA_ACCESS_EDITION || row.original.app_id === APP_ID_SDWAN) {
            if (row.original.is_license_remaining) {
              items.push({ value: ACTION_BUTTON_CLOUD })
            }
          }
          else if (row.original.app_id === AIOPS_APP_ID) {
            items.push({ value: ACTION_BUTTON_DAS })
          }
          return <Select
            addClassName='cmm-modal-select'
            button={
              <Button isMenu appearance='secondary' icon={<MoreIcon size="md" />}>
              </Button>
            }
            items={items}
            onChange={(e) => onActionSelect(e, row)}
            selectedItem={[]}
          />
        },
        width: 100,
        sticky: true,
      },
    ]
  }, [DisplayNameRenderer, associatedTenantModal, contractModal, onActionSelect])

  return (
    <div className='cm-w-full cm-relative'>
      <TABLE_CONTAINER className='cm-absolute cm-px-8 cm-inset-0'>
        <TableProvider
          columns={columns as any}
          data={data}
          enableColumnSort
          enableResizeColumns
          enablePagination
          isLoading={loading}
        // globalFilter={globalFilter}
        >
          <TableHeader title='Subscriptions'>
            <ConnectedTableFilter />
          </TableHeader>
          <ConnectedTableLayout
            overflow
            sidecar={<div />}
            paginator={<ConnectedPaginator pageSizeOptions={[10, 25, 50, 100, 500]} />}
          >
            <ConnectedTable
              appearance='primary'
              rowSize="cozy"
              emptyStateProps={{
                heading: 'No Available Licenses',
              }}
            />
          </ConnectedTableLayout>
        </TableProvider>
      </TABLE_CONTAINER>
    </div>
  )
}

export default LicenseTable
